import React, { useState, useEffect } from 'react';
import { MapPin, Mail, Lock, User, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from '../utils/toast';
import RotatingBackground from '../components/RotatingBackground';
import { useAuth } from '../contexts/AuthContext';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // 'login', 'register', or 'forgot'
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard');
  }, [isAuthenticated, navigate]);

  const handleGoogleLogin = async () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const isNative = Capacitor.isNativePlatform();
    const redirectUrl = isNative
      ? 'com.igoglobal.travel://oauth/callback'
      : window.location.origin + '/';
    const platform = isNative ? 'native' : 'web';
    const loginUrl = `${backendUrl}/api/auth/google/login?redirect=${encodeURIComponent(redirectUrl)}&platform=${platform}`;

    if (isNative) {
      try {
        await Browser.open({ url: loginUrl, windowName: '_system' });
      } catch (err) {
        console.error('Browser.open failed:', err);
        toast.error('Unable to open sign-in page. Please ensure Chrome is installed.');
      }
    } else {
      window.location.href = loginUrl;
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (mode === 'register' && name.trim().length < 1) {
      toast.error('Please enter your name');
      return;
    }
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const endpoint = mode === 'register' ? '/auth/register' : '/auth/email-login';
      const payload = mode === 'register'
        ? { name: name.trim(), email, password }
        : { email, password };

      const res = await axios.post(`${API}${endpoint}`, payload);
      const { session_token, user } = res.data;

      localStorage.setItem('session_token', session_token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('auth_timestamp', Date.now().toString());

      toast.success(mode === 'register' ? `Welcome, ${user.name}!` : `Welcome back, ${user.name}!`);
      window.location.href = '/dashboard';
    } catch (err) {
      const msg = err.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      toast.success('Reset instructions sent! Check your inbox.');
      setForgotSent(true);
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.message || 'Something went wrong. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <RotatingBackground showCaption={false} interval={8000}>
      <div className="min-h-screen flex items-center justify-center px-4" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="w-full max-w-md">
          {/* Back button */}
          <button
            onClick={() => navigate('/')}
            data-testid="login-back-btn"
            className="flex items-center gap-2 text-white/70 hover:text-white mb-6 transition-colors"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            <ArrowLeft size={18} />
            <span className="text-sm">Back</span>
          </button>

          {/* Card */}
          <div className="bg-black/50 backdrop-blur-xl border border-white/20 rounded-2xl p-6 compact:p-8">
            {/* Logo */}
            <div className="flex items-center justify-center gap-2 mb-6">
              <MapPin className="text-white" size={28} />
              <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>iGo</span>
            </div>

            <h1
              className="text-xl compact:text-2xl font-bold text-white text-center mb-2"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              {mode === 'forgot' ? 'Reset Password' : mode === 'login' ? 'Welcome Back' : 'Create Account'}
            </h1>
            <p className="text-sm text-white/60 text-center mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
              {mode === 'forgot' ? 'Enter your email to receive a reset link' : mode === 'login' ? 'Sign in to access your trips' : 'Start planning your next adventure'}
            </p>

            {/* Forgot password flow */}
            {mode === 'forgot' ? (
              forgotSent ? (
                <div className="text-center py-4">
                  <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                    <Mail size={24} className="text-green-400" />
                  </div>
                  <p className="text-white/80 text-sm mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>Check your inbox!</p>
                  <p className="text-white/50 text-xs mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
                    If an account exists for <strong className="text-white/70">{email}</strong>, we've sent a password reset link. It expires in 1 hour.
                  </p>
                  <Button
                    onClick={() => { setMode('login'); setForgotSent(false); setPassword(''); }}
                    data-testid="back-to-login-after-forgot"
                    className="bg-[var(--md3-primary)] hover:bg-[var(--md3-primary-hover)] text-white rounded-xl px-6 py-3"
                  >
                    Back to Sign In
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-3">
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <Input
                      type="email"
                      placeholder="Email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="forgot-email-input"
                      className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl h-11 focus:border-[var(--md3-primary)] focus:ring-[var(--md3-primary)]"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="forgot-submit-btn"
                    className="w-full bg-[var(--md3-primary)] hover:bg-[var(--md3-primary-hover)] text-white font-semibold py-5 rounded-xl transition-all"
                  >
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </Button>
                  <p className="text-center mt-4 text-sm text-white/60" style={{ fontFamily: 'Inter, sans-serif' }}>
                    <button
                      onClick={() => { setMode('login'); setPassword(''); }}
                      data-testid="forgot-back-to-login"
                      className="text-[var(--md3-primary)] hover:underline font-medium"
                    >
                      Back to Sign In
                    </button>
                  </p>
                </form>
              )
            ) : (
            <>
            {/* Google Sign In */}
            <Button
              onClick={handleGoogleLogin}
              data-testid="google-login-btn"
              className="w-full bg-white hover:bg-gray-100 text-gray-800 font-medium py-5 rounded-xl flex items-center justify-center gap-3 transition-all mb-5"
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </Button>

            {/* Divider */}
            <div className="flex items-center gap-3 mb-5">
              <div className="flex-1 h-px bg-white/20"></div>
              <span className="text-xs text-white/50" style={{ fontFamily: 'Inter, sans-serif' }}>or</span>
              <div className="flex-1 h-px bg-white/20"></div>
            </div>

            {/* Email Form */}
            <form onSubmit={handleEmailSubmit} className="space-y-3">
              {mode === 'register' && (
                <div className="relative">
                  <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                  <Input
                    type="text"
                    placeholder="Full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="register-name-input"
                    className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl h-11 focus:border-[var(--md3-primary)] focus:ring-[var(--md3-primary)]"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  />
                </div>
              )}

              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <Input
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl h-11 focus:border-[var(--md3-primary)] focus:ring-[var(--md3-primary)]"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                />
              </div>

              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="pl-10 pr-10 bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl h-11 focus:border-[var(--md3-primary)] focus:ring-[var(--md3-primary)]"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit-btn"
                className="w-full bg-[var(--md3-primary)] hover:bg-[var(--md3-primary-hover)] text-white font-semibold py-5 rounded-xl transition-all"
              >
                {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
              </Button>

              {/* Forgot password link - login mode only */}
              {mode === 'login' && (
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => setMode('forgot')}
                    data-testid="forgot-password-link"
                    className="text-xs text-white/50 hover:text-[var(--md3-primary)] transition-colors"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    Forgot password?
                  </button>
                </div>
              )}
            </form>

            {/* Toggle mode */}
            <p className="text-center mt-5 text-sm text-white/60" style={{ fontFamily: 'Inter, sans-serif' }}>
              {mode === 'login' ? (
                <>
                  Don't have an account?{' '}
                  <button
                    onClick={() => { setMode('register'); setName(''); setPassword(''); }}
                    data-testid="switch-to-register"
                    className="text-[var(--md3-primary)] hover:underline font-medium"
                  >
                    Sign Up
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{' '}
                  <button
                    onClick={() => { setMode('login'); setPassword(''); }}
                    data-testid="switch-to-login"
                    className="text-[var(--md3-primary)] hover:underline font-medium"
                  >
                    Sign In
                  </button>
                </>
              )}
            </p>
            </>
            )}
          </div>
        </div>
      </div>
    </RotatingBackground>
  );
};

export default LoginPage;
